"use client";

import { useState } from "react";
import { Download, ExternalLink } from "@/components/icons";
import { motion, useReducedMotion } from "@/lib/motion";
import { HeroGlow } from "@/components/ui";
import LogoBuilderDialog from "./logo-builder";
import type { BuilderPreset } from "./logo-builder/types";
import {
  COLOR_GROUPS,
  ICON_SAMPLES,
  LOGO_DONTS,
  LOGO_DOS,
  LOGO_SYMBOLS,
  LOGO_VARIANTS,
  MOTION_PRINCIPLES,
  OVERVIEW_STATS,
  TYPE_SPECS,
  VOICE_PILLARS,
} from "./data";
import {
  ColorGroupBlock,
  GuidelineHeader,
  IconTile,
  LogoCard,
  LogoRule,
  PrincipleCard,
  SymbolCard,
  TypeRow,
} from "./parts";

export default function BrandGuidelinesSection() {
  const prefersReducedMotion = useReducedMotion();
  const [builderPreset, setBuilderPreset] = useState<BuilderPreset | null>(null);

  return (
    <main id="main-content" tabIndex={-1} className="relative min-h-screen overflow-y-clip">
      <section className="relative overflow-y-clip pt-28 pb-16 sm:pt-32 lg:pt-40 lg:pb-24">
        <HeroGlow align="center" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto max-w-3xl text-center"
          >
            <p className="text-muted-foreground mb-4 text-sm font-medium">Brand Guidelines</p>
            <h1 className="text-foreground text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
              The shape of Proxidize.
            </h1>

            <p className="text-muted-foreground light:text-foreground/80 mx-auto mt-6 max-w-2xl text-base leading-relaxed sm:text-lg">
              A living reference for how we look, sound, and move — covering the logo, palette, type
              system, iconography, motion, and voice that hold the product together.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setBuilderPreset({ asset: "wordmark", color: "white" })}
                className="bg-foreground text-background inline-flex h-10 cursor-pointer items-center gap-2 rounded-md px-4 text-sm font-medium transition-opacity hover:opacity-90"
              >
                <Download size={16} />
                Download wordmark
              </button>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-4 text-left sm:grid-cols-4">
              {OVERVIEW_STATS.map((s) => (
                <div key={s.label} className="bg-card border-border rounded-xl border px-5 py-4">
                  <p className="text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
                    {s.label}
                  </p>
                  <p className="text-foreground mt-1.5 text-lg font-semibold tabular-nums">
                    {s.value}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Body ──────────────────────────────────────────────── */}
      <div className="relative mx-auto max-w-5xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <div>
          <div className="min-w-0 space-y-24 lg:space-y-32">
            {/* Overview */}
            <section id="overview" className="scroll-mt-28">
              <GuidelineHeader
                index="01"
                eyebrow="Foundations"
                title="A dark-only system with two signals."
                body="Proxidize is built on near-black surfaces, with brand yellow and brand green as the only colors allowed to draw attention. Everything else — type, hairlines, motion — exists in service of that contrast."
              />
              <div className="border-border bg-card rounded-2xl border p-6 sm:p-8">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {[
                    {
                      title: "Restraint",
                      body: "One accent, one signal color. The rest is hierarchy and whitespace.",
                    },
                    {
                      title: "Density",
                      body: "Information close together, breathing where it counts. Engineers read fast.",
                    },
                    {
                      title: "Confidence",
                      body: "Direct copy. Specific numbers. No marketing filler. The product earns the claim.",
                    },
                  ].map((p) => (
                    <div key={p.title} className="bg-surface-2 border-border rounded-xl border p-6">
                      <p className="text-foreground text-sm font-semibold">{p.title}</p>
                      <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{p.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Logo */}
            <section id="logo" className="scroll-mt-28">
              <GuidelineHeader
                index="02"
                eyebrow="Identity"
                title="The wordmark, in two contexts."
                body="The Proxidize wordmark is the only approved mark. Use the lockup as a single asset — never reconstruct the glyphs."
              />
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {LOGO_VARIANTS.map((v) => (
                  <LogoCard key={v.id} variant={v} onDownload={setBuilderPreset} />
                ))}
              </div>
              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                {LOGO_SYMBOLS.map((s) => (
                  <SymbolCard key={s.src} symbol={s} onDownload={setBuilderPreset} />
                ))}
              </div>
            </section>

            {/* Logo usage */}
            <section id="logo-usage" className="scroll-mt-28">
              <GuidelineHeader
                index="03"
                eyebrow="Usage"
                title="Clearspace, contrast, and the rules we don't bend."
                body="The wordmark is small surface area on the page — give it room to breathe and don't decorate it."
              />
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <LogoRule tone="do" title="Do" items={LOGO_DOS} />
                <LogoRule tone="dont" title="Don't" items={LOGO_DONTS} />
              </div>
            </section>

            {/* Color */}
            <section id="color" className="scroll-mt-28">
              <GuidelineHeader
                index="04"
                eyebrow="Palette"
                title="Surfaces and brand primitives."
                body="The color system is intentionally narrow. Surfaces handle hierarchy, brand primitives handle attention — that's the whole palette."
              />
              <div>
                {COLOR_GROUPS.map((g) => (
                  <ColorGroupBlock key={g.id} group={g} />
                ))}
              </div>
            </section>

            {/* Typography */}
            <section id="typography" className="scroll-mt-28">
              <GuidelineHeader
                index="05"
                eyebrow="Type"
                title="Geist Sans for everything, Geist Mono for code."
                body="One sans family, one mono family. Weight and size carry hierarchy — never decorative typefaces."
              />
              <div className="space-y-5">
                {TYPE_SPECS.map((spec) => (
                  <TypeRow key={spec.role} spec={spec} />
                ))}
              </div>
            </section>

            {/* Iconography */}
            <section id="iconography" className="scroll-mt-28">
              <GuidelineHeader
                index="06"
                eyebrow="Icons"
                title="Lucide, 1.6 stroke, no fills."
                body="Every product icon comes from the Lucide set, sized at 18–20px with a 1.6 stroke weight. Consistency over cleverness."
              />
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-5">
                {ICON_SAMPLES.map((s) => (
                  <IconTile key={s.name} sample={s} />
                ))}
              </div>
              <div className="border-border bg-card/60 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-5">
                <p className="text-muted-foreground text-sm">
                  Import from{" "}
                  <code className="text-foreground bg-surface-2 rounded px-1.5 py-0.5 font-mono text-xs">
                    @/components/icons
                  </code>{" "}
                  — never directly from{" "}
                  <code className="text-foreground bg-surface-2 rounded px-1.5 py-0.5 font-mono text-xs">
                    lucide-react
                  </code>
                  .
                </p>
                <a
                  href="https://lucide.dev/icons/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground inline-flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-80"
                >
                  Browse full set
                  <ExternalLink size={12} />
                </a>
              </div>
            </section>

            <section id="motion" className="scroll-mt-28">
              <GuidelineHeader
                index="07"
                eyebrow="Motion"
                title="Calm reveals. Pulsing accents."
                body="Motion is mostly invisible. When it shows up, it carries meaning — never decoration."
              />
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {MOTION_PRINCIPLES.map((p, i) => (
                  <PrincipleCard key={p.title} item={p} index={i} accent={false} />
                ))}
              </div>
            </section>

            <section id="voice" className="scroll-mt-28">
              <GuidelineHeader
                index="08"
                eyebrow="Voice"
                title="How Proxidize sounds."
                body="Confident, technical, plain. We write the way engineers read — fast, specific, and without fanfare."
              />
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {VOICE_PILLARS.map((p, i) => (
                  <PrincipleCard key={p.title} item={p} index={i} />
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      <LogoBuilderDialog
        open={builderPreset !== null}
        onOpenChange={(open) => {
          if (!open) setBuilderPreset(null);
        }}
        preset={builderPreset ?? undefined}
      />
    </main>
  );
}
